// The drift screen's vocabulary and its one shared derivation.
//
// `providerDriftQuery` is here rather than in the screen for the reason
// `event-ranges.ts` exists: `adminProviderDrift` declares no `keyOf`, so **the
// whole query object is the cache key**. A loader that builds `{since_ms: 7d}`
// and a screen that builds `{since_ms: 7d, campaign_id: undefined}` would agree
// by luck of how the key is hashed, and a loader that guessed `{}` would not
// agree at all — it would block first paint on a read no component subscribes
// to and then mount cold, which is strictly worse than having no loader. One
// function, called by both sides, makes the disagreement unrepresentable.
//
// The rest is the vocabulary. A drift row is two values and a verdict, and each
// of the three has a state that is NOT a value — `expected_value` absent means
// the plan does not model the field, `observed_value` absent means the setting
// was removed at the provider, and neither closing column set means the field
// was still diverging at the last sighting. Rendering any of those as an empty
// cell loses the finding.
//
// One row per (object, field) for as long as it keeps diverging: the sweep bumps
// `last_detected_at` instead of adding a row, and closes it one of three ways —
// `repaired_at` (SAM's own verb wrote it), `resolution = 'observed'` (a later
// pass saw the provider back in line: fixed by hand, or by the provider), or
// `resolution = 'superseded'` (the plan moved to a newer revision).

import type { ProviderDriftEvent, ProviderDriftScan } from '@/api/types';
import type { DriftFindingStatus, DriftVerdict } from '@/api/types.gen';
import type { ProviderDriftQuery } from '@/api/resources';

export const DRIFT_WINDOWS = ['1h', '24h', '7d', '30d'] as const;
export type DriftWindow = (typeof DRIFT_WINDOWS)[number];

/**
 * What the screen opens on, and therefore what its loader must warm.
 *
 * 7 days on purpose: it is also the API's `DEFAULT_WINDOW_MS`, so the screen
 * and a hand-rolled `curl` see the same set, and it is the window the sweep's
 * own 14-day recurrence escalation is measured against.
 */
export const DRIFT_SCREEN_DEFAULT_WINDOW: DriftWindow = '7d';

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

const WINDOW_MS: Record<DriftWindow, number> = {
  '1h': HOUR_MS,
  '24h': DAY_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
};

export const DRIFT_WINDOW_LABEL: Record<DriftWindow, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

/**
 * The one place a drift query object is built.
 *
 * `campaign_id` is OMITTED rather than set to `undefined` when there is no
 * campaign filter, so the key and the URL are built from the same shape — and
 * an unrecognised window falls back to the screen's default rather than to `{}`,
 * because the window can come straight from a URL a person typed.
 */
export function providerDriftQuery(
  window: DriftWindow,
  campaignId?: string,
  filters: { view?: 'unresolved' | 'history'; target?: string; lifecycle?: string; status?: string; cursor?: string } = {},
): ProviderDriftQuery {
  const safe: DriftWindow = DRIFT_WINDOWS.includes(window)
    ? window
    : DRIFT_SCREEN_DEFAULT_WINDOW;
  const since_ms = WINDOW_MS[safe];
  return { since_ms, ...(campaignId ? { campaign_id: campaignId } : {}), ...filters };
}

/**
 * The four states a row can be in, derived from its closing columns. Typed evidence decides actionability; the closing columns preserve legacy compatibility.
 */
export type DriftState = 'open' | 'repaired' | 'resolved' | 'superseded';

export const DRIFT_STATE_LABEL: Record<DriftState, string> = {
  open: 'OPEN',
  repaired: 'REPAIRED',
  resolved: 'RESOLVED',
  superseded: 'SUPERSEDED',
};

export function driftState(e: Pick<ProviderDriftEvent, 'repaired_at' | 'resolved_at' | 'resolution'>): DriftState {
  if (e.repaired_at != null) return 'repaired';
  if (e.resolved_at != null) return e.resolution === 'superseded' ? 'superseded' : 'resolved';
  return 'open';
}

/** One row as the table reads it: epochs coerced once, verdict derived once. */
export interface DriftRow {
  evidence?: ProviderDriftEvent['evidence'];
  id: string;
  /** The pass that opened the row. */
  scanId: string;
  /** The pass that last saw it diverging. */
  lastScanId: string;
  campaignId: string;
  campaignRevision: number;
  target: string;
  objectKind: string;
  providerObjectId: string;
  field: string;
  expected: string | null;
  observed: string | null;
  /** First sighting. */
  detectedAtMs: number;
  /** Last sighting — what the list is windowed and ordered on. */
  lastDetectedAtMs: number;
  repairedAtMs: number | null;
  resolvedAtMs: number | null;
  resolution: string | null;
  hasPreImage: boolean;
  state: DriftState;
  /** The actionable state: still diverging at the last sighting. */
  open: boolean;
}

// The generated bindings type the epochs as `bigint` (they are `i64` in Rust)
// while JSON delivers plain numbers. Coercing once here means no cell has to
// remember which it is holding — the same trick `capi-forwarding.tsx` plays for
// its counts.
function ms(v: bigint | number): number {
  return Number(v);
}

/**
 * Most recently seen first, and stated here rather than trusted from the wire.
 *
 * The API already sorts `last_detected_at DESC, id` — this mirrors the SAME
 * total order, tiebreak included, so a client-side filter or a future merge of
 * two windows cannot quietly reorder the list. A stable tiebreak matters because
 * a scan stamps every field of one object with an identical sighting.
 */
export function toDriftRows(events: readonly ProviderDriftEvent[]): DriftRow[] {
  return events
    .map((e) => {
      const state = driftState(e);
      return {
        id: e.id,
        evidence: e.evidence,
        scanId: e.scan_id,
        lastScanId: e.last_scan_id,
        campaignId: e.campaign_id,
        campaignRevision: e.campaign_revision,
        target: e.target,
        objectKind: e.object_kind,
        providerObjectId: e.provider_object_id,
        field: e.field,
        expected: e.expected_value,
        observed: e.observed_value,
        detectedAtMs: ms(e.detected_at),
        lastDetectedAtMs: ms(e.last_detected_at),
        repairedAtMs: e.repaired_at == null ? null : ms(e.repaired_at),
        resolvedAtMs: e.resolved_at == null ? null : ms(e.resolved_at),
        resolution: e.resolution ?? null,
        hasPreImage: e.has_pre_image,
        state,
        open: state === 'open',
      };
    })
    .sort((a, b) =>
      b.lastDetectedAtMs - a.lastDetectedAtMs || a.id.localeCompare(b.id));
}

export interface DriftFilters {
  /** `all`, or a provider `target` exactly as the row carries it. */
  target: string;
  onlyOpen: boolean;
}

/** Client-side only — neither field reaches the query, so neither may key it. */
export function filterDriftRows(
  rows: readonly DriftRow[],
  filters: DriftFilters,
): DriftRow[] {
  return rows.filter(
    (row) =>
      (filters.target === 'all' || row.target === filters.target) &&
      (!filters.onlyOpen || row.open),
  );
}

export interface DriftSummary {
  total: number;
  /** Unresolved at the last sighting; typed evidence determines actionability. */
  open: number;
  /** Closed by observation: the provider came back in line on its own. */
  resolved: number;
  /** Distinct sweep passes that OPENED a row. Six fields on one pass is not six incidents. */
  scans: number;
  campaigns: number;
}

export function summarizeDrift(rows: readonly DriftRow[]): DriftSummary {
  return {
    total: rows.length,
    open: rows.filter((r) => r.open).length,
    resolved: rows.filter((r) => r.state === 'resolved').length,
    scans: new Set(rows.map((r) => r.scanId)).size,
    campaigns: new Set(rows.map((r) => r.campaignId)).size,
  };
}

export type DriftSide = 'expected' | 'observed';

/**
 * What an absent value MEANS, per side.
 *
 * Both sides are nullable and they say opposite things: no expected value means
 * the plan does not carry the field at all (so the sweep found something it
 * cannot have an opinion about), and no observed value means the setting is
 * gone from the provider (removed, not changed). One shared "—" would collapse
 * two different findings into the same cell.
 */
const ABSENT_LABEL: Record<DriftSide, string> = {
  expected: 'not in the plan',
  observed: 'absent at the provider',
};

export function driftSideLabel(value: string | null, side: DriftSide): string {
  return value ?? ABSENT_LABEL[side];
}

const TARGET_LABEL: Record<string, string> = { meta: 'META', ttd: 'TTD' };

const OBJECT_LABEL: Record<string, string> = {
  campaign: 'Campaign',
  ad_set: 'Ad set',
  ad: 'Ad',
  ad_group: 'Ad group',
  creative: 'Creative',
};

// Both fall back to the raw string. These arrive as free `TEXT` from D1, so a
// provider or object kind added server-side must render as itself rather than
// as a blank cell in the one screen that would show it first.
export function driftTargetLabel(target: string): string {
  return TARGET_LABEL[target] ?? target.toUpperCase();
}

export function driftObjectLabel(kind: string): string {
  return OBJECT_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

// ─── Coverage ───────────────────────────────────────────────────────────────
//
// Findings alone cannot answer "is everything fine". An empty events list is
// equally consistent with three very different situations: every object
// matches, the sweep has never run, or it has failed at the provider on every
// attempt for two days. The screen used to report the first one for all three.
//
// `scans` in `DriftSummary` has the same shape of problem and is left alone
// deliberately — it counts distinct `scan_id`s *among findings*, which is a
// useful "how many passes found something" and a terrible denominator. The
// denominator lives here.

export interface DriftCoverage {
  /** Sweep passes recorded, whether or not they found anything. */
  scans: number;
  /** Completed and found nothing. */
  clean: number;
  /** Completed and found drift. */
  drifted: number;
  /**
   * Completed, but the published hierarchy was missing objects the plan
   * expects — so a clean verdict would not have meant anything.
   */
  incomplete: number;
  /** Errored at the provider or in D1. These are why "no drift" can lie. */
  failed: number;
  /** Claimed and still in flight. */
  running: number;
  /** Newest `updated_at` across every scan, or null when none has ever run. */
  lastScanAt: number | null;
}

export function summarizeCoverage(
  scans: readonly ProviderDriftScan[],
): DriftCoverage {
  const count = (predicate: (s: ProviderDriftScan) => boolean) =>
    scans.filter(predicate).length;
  return {
    scans: scans.length,
    clean: count((s) => s.coverage?.verdict === 'clean'),
    drifted: count((s) => s.status === 'complete' && s.state === 'drifted'),
    incomplete: count((s) => !s.coverage || ['verification_overdue', 'unverified'].includes(s.coverage.verdict)),
    failed: count((s) => s.status === 'failed'),
    running: count((s) => s.status === 'running'),
    lastScanAt: scans.length
      ? scans.reduce((newest, s) => Math.max(newest, Number(s.updated_at)), 0)
      : null,
  };
}

/**
 * What an empty findings list is actually entitled to say.
 *
 * The old copy — "every published object still matches its plan" — is a claim
 * about the world derived from the absence of rows. It is true only when every
 * scan completed and none was incomplete. Anything else and the honest answer
 * is that nothing was found, which is not the same thing.
 */
export function emptyDriftMessage(
  coverage: DriftCoverage,
  campaignScoped: boolean,
): string {
  if (coverage.scans === 0) {
    return 'No drift recorded — and no sweep has completed yet, so this is not yet a clean bill of health.';
  }
  if (coverage.failed > 0) {
    return `No drift recorded, but ${coverage.failed} scan${coverage.failed === 1 ? '' : 's'} failed at the provider — what they would have found is unknown.`;
  }
  if (coverage.incomplete > 0) {
    return `No drift recorded, but ${coverage.incomplete} scan${coverage.incomplete === 1 ? '' : 's'} could not read the whole published hierarchy.`;
  }
  return campaignScoped
    ? 'No drift recorded for this campaign in this window.'
    : 'No findings match this view. Coverage below describes the latest verification.';
}

export const DRIFT_FINDING_LABEL: Record<DriftFindingStatus, string> = {
  action_required: 'Action required', repairing: 'Repairing', recovery_blocked: 'Recovery blocked',
  known_limitation: 'Known limitation', verification_overdue: 'Verification overdue',
  replaced: 'Replaced', superseded: 'Superseded', repaired: 'Repaired', resolved: 'Resolved',
};
export const DRIFT_VERDICT_LABEL: Record<DriftVerdict, string> = {
  clean: 'Checked — no drift', known_limitations: 'Checked — known limitations',
  action_required: 'Action required', repairing: 'Repairing', recovery_blocked: 'Recovery blocked',
  verification_overdue: 'Verification overdue', unverified: 'Status unverified',
};
export function driftCoverageLabel(scan?: ProviderDriftScan): string {
  return scan?.coverage ? DRIFT_VERDICT_LABEL[scan.coverage.verdict] : 'Status unverified';
}
